import React, { useContext, useState } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  OrderType,
} from '../../../interfaces/delivery-company/orders';
import { DeliveryState } from '../../../enums/orders';
import { useParams } from 'react-router-dom';
import {
  showDeliveryState,
  showPaymentState,
} from '../../common/styles/order-states.style';
import {
  cardTableStyle,
} from '../../common/styles/grid-card.style';
import axios, { AxiosResponse } from 'axios';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import { AuthContext } from '../../../contexts/user-context/user-context';
import ClearIcon from '@mui/icons-material/Clear';

import { API_ENDPOINT } from '../../../configurations/global.config';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import { isMobile, isTablet } from 'react-device-detect';
import {
  parseOrderEventActor,
  parseOrderEventChanges,
} from '../../../utils/orderevent';
import OrderTimeline from './order-timeline';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import OrderNotes from '../../common/order-notes/order-notes';
import { useSnackBar } from '../../../contexts/snackbar';
import MerchantCreateOrderForm from '../merchant-orders/merchant-create-order-form/merchant-create-order-form';
import {
  OrderAction,
  createEditAction,
  createCancelAction,
} from '../../common/order-page-utils/order-actions-header';
import OrderDetailsWithMap from '../../common/order-page-utils/order-details-with-map';
import CodUpdateForm from '../../common/cod-update/cod-update-form/cod-update-form';
import CodUpdateDialog from '../../common/cod-update/cod-update-dialog/cod-update-dialog';
import {
  getPackageDescription,
  renderPackageDetailsRows,
} from '../../../utils/order';

const MerchantOrder = () => {
  const { orderId } = useParams();
  const [order, setOrder] = React.useState<OrderType>();
  const [confirmDeleteOrder, setConfirmDeleteOrder] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [response, setResponse] = React.useState<AxiosResponse>();
  const [message, setMessage] = React.useState<string>('');
  const { config } = React.useContext(AuthContext);
  const { color } = useContext(OrgSettingsContext);
  const [editingOrder, setEditingOrder] = useState(false);
  const { showMessage } = useSnackBar();

  React.useEffect(() => {
    const getAndSetOrder = async () => {
      if (orderId !== undefined) {
        setIsLoading(true);

        try {
          const endpoint = `${API_ENDPOINT}/api/v1/order/${orderId}/`;
          const response = await axios.get<OrderType>(endpoint, config);
          setResponse(response);
          setOrder(response.data);
        } catch (e) {
          setOrder(undefined);
          if (axios.isAxiosError(e)) {
            setMessage('Failed to load order data: ' + e.response?.statusText);
            setResponse(e.response);
          } else {
            setMessage('Failed to load order data');
          }
        }
        setIsLoading(false);
      }
    };
    getAndSetOrder();
  }, [orderId]);

  const deleteOrder = async (reason?: string) => {
    if (order && order.id !== '') {
      setIsLoading(true);
      setResponse(undefined);
      try {
        const response = await axios.delete(
          `${API_ENDPOINT}/api/v1/order/${order.id}/?reason=${encodeURIComponent(reason ?? 'not specified')}`,
          config
        );
        setResponse(response);
        setOrder({ ...order, delivery_state: DeliveryState.CANCELLED });
        setMessage('Order cancelled successfully');
        showMessage('Order cancelled successfully', 'success');
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to cancel order: ' + e.response?.statusText);
          showMessage('Failed to cancel order', 'error');
        } else {
          setMessage('Failed to cancel order');
        }
      }
      setIsLoading(false);
    }
  };

  /** Build merchant-specific actions for the order header */
  const buildOrderActions = (): { primary: OrderAction[], secondary: OrderAction[] } => {
    if (!order) return { primary: [], secondary: [] };

    const canEdit = [
      DeliveryState.CREATED,
      DeliveryState.PICKUP_REQUESTED,
      DeliveryState.PICKUP_ASSIGNED,
    ].includes(order.delivery_state);

    const canCancel = [
      DeliveryState.CREATED,
      DeliveryState.PICKUP_REQUESTED,
      DeliveryState.PICKUP_ASSIGNED,
    ].includes(order.delivery_state);

    const primary: OrderAction[] = [];
    const secondary: OrderAction[] = [];

    // Edit Order - Primary action when available
    if (canEdit) {
      primary.push(createEditAction(true, () => setEditingOrder(true)));
    }

    // Cancel Order - Secondary action
    if (canCancel) {
      secondary.push(createCancelAction(true, () => setConfirmDeleteOrder(true)));
    }

    return { primary, secondary };
  };

  return (
    <div>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          {message !== '' && (
            <>
              <Alert
                severity={
                  response &&
                  response.status >= 200 &&
                  response.status < 300
                    ? 'success'
                    : 'error'
                }
              >
                <>
                  {message}

                  {response && response.status >= 300 && (
                    <ErrorsFlattenner rawErrors={response.data} />
                  )}
                </>
              </Alert>
              <br />
            </>
          )}

          {!order ? (
            <Alert severity="error">
              An unexpected error occurred, please contact us to resolve the
              issue.
            </Alert>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  pb: 0,
                  borderRadius: '12px',
                  gap: 1,
                }}
              >
                {/* Use OrderDetailsWithMap for consistent structure */}
                {(() => {
                  const actions = buildOrderActions();
                  return (
                    <OrderDetailsWithMap
                      order={order}
                      setOrder={setOrder}
                      additionalPrimaryActions={actions.primary}
                      additionalSecondaryActions={actions.secondary}
                    />
                  );
                })()}

                {/* Dialogs */}
                <ConfirmAction
                  message={'Are you sure you want to cancel this order?'}
                  requireReason
                  action={deleteOrder}
                  isOpen={confirmDeleteOrder}
                  isSevereAction
                  handleClose={() => setConfirmDeleteOrder(false)}
                >
                  <Alert severity="warning">
                    This action can not be undone.
                  </Alert>
                </ConfirmAction>

                <CustomDialog
                  fullWidth
                  maxWidth="sm"
                  open={editingOrder}
                  onClose={() => setEditingOrder(false)}
                  fullScreen={(isMobile && !isTablet)}
                >
                  <div
                    style={{
                      textAlign: 'right',
                      width: '100%',
                    }}
                  >
                    <IconButton onClick={() => setEditingOrder(false)}>
                      <ClearIcon color="primary" fontSize="large" />
                    </IconButton>
                  </div>

                  <DialogContent>
                    <MerchantCreateOrderForm defaultOrder={order} onSuccess={setOrder} />
                  </DialogContent>
                </CustomDialog>
              </Box>

              <Box mt={2}>
              <OrderTimeline
                order={order}
                statestamps={order.statestamps}
                events={order.events}
                color={color}
                showDeliveryState={showDeliveryState}
                showPaymentState={showPaymentState}
                parseOrderEventActor={parseOrderEventActor}
                parseOrderEventChanges={parseOrderEventChanges}
                pickup_tasks={order.pickup_tasks ?? []}
                delivery_tasks={order.delivery_tasks ?? []}
              />
              </Box>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MerchantOrder;
